'use client';

import { useState, useEffect } from 'react';
import Logo from '@/UIComponents/Logo/Logo';
import InputField from '@/UIlibrary/InputField/InputField';
import Button from '@/UIlibrary/Button/Button';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import {
	useUpdatePasswordMutation,
	UpdatePasswordMutation,
	UpdatePasswordInput
} from '@/graphql/__generated__/authentication';
import { toast } from 'react-hot-toast';
import useAuthStore from '@/lib/auth';
import logger from '@/logger';
import { validatePassword } from '@/utils/utils';

interface ResetPasswordErrors {
	password?: string;
	passwordConfirmation?: string;
}

const ResetPasswordForm: React.FC = () => {
	console.log('ResetPasswordForm component rendered'); // Debug log

	const { lang } = useParams();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');

	console.log('Token from URL:', token ? 'Present (non-null)' : 'Missing (null)'); // Debug log

	const [formData, setFormData] = useState({
		password: '',
		confirmPassword: ''
	});
	const { setToken, isAuthenticated } = useAuthStore();
	const [formErrors, setFormErrors] = useState<ResetPasswordErrors>({});
	const [formIsValid, setFormIsValid] = useState<boolean>(false);
	const t = useTranslations('ResetPassword');
	const navigate = useNavigate();

	console.log('Current auth state:', isAuthenticated() ? 'Authenticated' : 'Not authenticated'); // Debug log

	const authenticationClient = useApolloClient(SCHEMAS.AUTHENTICATION);
	const [updatePassword, { loading: loadingUpdatePassword }] = useUpdatePasswordMutation({
		client: authenticationClient,
		onCompleted: (data: UpdatePasswordMutation) => {
			const recoverData = data.updatePassword;
			if (recoverData?.successful) {
				logger.debug('Navigating to login page after successful password reset');
				toast.success(t('PasswordResetSuccessMessage'), { duration: 15000 });
				setToken(null);
				navigate(`/${lang}/login`, { replace: true });
			}
			if (recoverData?.mutationErrors && recoverData.mutationErrors.length > 0) {
				recoverData.mutationErrors.forEach((error) => {
					if (error) {
						switch (error.__typename) {
							case 'InvalidInput':
								setFormErrors((prev) => ({
									...prev,
									[error.field]: error.message
								}));
								break;
							case 'ActionFailed':
							case 'InvalidAction':
								toast.error(t('PasswordResetFailed'), { duration: 15000 });
								navigate(`/${lang}/signin`, { replace: true }); // Changed from /login to /signin
								break;
							default:
								logger.debug('Unhandled mutation error in recover password', error);
								break;
						}
					}
				});
			}
		},
		onError: () => {
			toast.error(t('PasswordResetFailed'), { duration: 15000 });
			navigate(`/${lang}/signin`, { replace: true }); // Changed from /login to /signin
		}
	});

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setFormErrors({});
		const resetPasswordInput: UpdatePasswordInput = {
			password: formData.password,
			passwordConfirmation: formData.confirmPassword
		};
		updatePassword({ variables: { input: resetPasswordInput } });
		console.log('Reset password form data:', formData);
	};

	useEffect(() => {
		console.log('Token useEffect triggered'); // Debug log

		if (token) {
			console.log('Setting token in auth store'); // Debug log
			setToken(token);
		}
	}, [token, setToken]);

	useEffect(() => {
		const passwordsMatch = formData.password === formData.confirmPassword;
		const noEmptyFields = Object.values(formData).every((value) => value !== '');
		const isPasswordValid = validatePassword(formData.password);

		setFormIsValid(noEmptyFields && passwordsMatch && isPasswordValid);

		setFormErrors((prev) => {
			const updated = { ...prev };

			if (!isPasswordValid && formData.password.length > 0) {
				updated.password = t('PasswordFormatDisclaimer');
			} else {
				delete updated.password;
			}

			if (!passwordsMatch && formData.confirmPassword.length > 0) {
				updated.passwordConfirmation = t('PasswordsDoNotMatch');
			} else {
				delete updated.passwordConfirmation;
			}

			return updated;
		});
	}, [formData, t]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prevFormData) => ({
			...prevFormData,
			[name]: value
		}));
	};

	return (
		<div className="w-full h-screen flex flex-col justify-center items-center">
			<form
				className="flex flex-col w-11/12 md:w-full max-w-[550px] mx-auto bg-[#FFFFFF] py-12 px-11 rounded-[25px] gap-8"
				onSubmit={handleSubmit}
			>
				<div className="flex flex-row justify-between">
					<Logo src="/logo-square-white.jpg" width={110} height={110} />
				</div>
				<div className="flex flex-col gap-8">
					<div className="flex flex-col gap-3">
						<Typography type="display" size="sm" weight="semibold">
							{t('ResetPassword')}
						</Typography>
					</div>
					<div className="flex flex-col gap-2">
						<InputField
							name="password"
							type="password"
							label={t('NewPassword')}
							value={formData.password}
							onChange={handleChange}
							loading={loadingUpdatePassword}
							size={'auto'}
							placeholder={t('NewPasswordPlaceholder')}
							hint={t('PasswordFormatDisclaimer') || formErrors.password}
							error={formErrors.password ? true : false}
						/>
						<InputField
							name="confirmPassword"
							type="password"
							label={t('ConfirmPassword')}
							value={formData.confirmPassword}
							onChange={handleChange}
							loading={loadingUpdatePassword}
							disabled={loadingUpdatePassword}
							placeholder={t('ConfirmPassword')}
							size={'auto'}
							hint={formErrors.passwordConfirmation}
							error={formErrors.passwordConfirmation ? true : false}
						/>
						<Button
							type="submit"
							disabled={!formIsValid || loadingUpdatePassword}
							label={t('Reset')}
							size="auto"
						/>
					</div>
				</div>
			</form>
		</div>
	);
};

export default ResetPasswordForm;
